/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Add from '@material-ui/icons/Add';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsAlt, dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import {SimpleChip} from './SimpleChip';
import {QueryChipData} from './types';
import {isExactQuery} from './utils';

interface QueryChipsProps {
  queries: QueryChipData[];
  pageName: string;
  onRemove?: Function;
  disabled?: boolean;
  showCount?: number;
  onClickQueryChip: (queryChipData: any) => void;
}

const styles = makeStyles(() => ({
  root : {
    marginBottom   : '10px',
    overflow       : 'unset',
    justifyContent : 'space-between',
    '&:hover'      : {
      backgroundColor : `${dlsColor('grey5')} !important`
    }
  },
  paper : {
    borderRadius : 8,
    boxShadow    : dlsAlt('components', 'boxShadow', 'shadow4')
  },
  deleteIcon : {
    marginTop : '6px'
  },
  list : {
    right      : toggleThemeStyle('0', '8px'),
    width      : 'auto',
    height     : 'auto',
    margin     : '8px 0 0px 8px',
    display    : 'grid',
    maxWidth   : '400px',
    minWidth   : '180px',
    maxHeight  : '300px',
    minHeight  : '64px',
    position   : toggleThemeStyle('unset', 'relative'),
    alignItems : toggleThemeStyle('unset', 'flex-start')
  },
  iconButton : {
    top        : toggleThemeStyle('0', '4px'),
    left       : toggleThemeStyle('0', '10px'),
    color      : dlsColor('grey3'),
    padding    : '0 !important',
    fontSize   : 12,
    position   : toggleThemeStyle('unset', 'relative'),
    fontWeight : 700,
    whiteSpace : 'nowrap',
    '&:hover'  : {
      background : dlsColor('transparent')
    },
    '& .MuiButton-label' : {
      marginBottom : '10px'
    }
  },
  listItem : {
    height     : 40,
    minHeight  : 'auto',
    alignItems : 'baseline'
  },
  chipRoot : toggleThemeStyle({
    background    : dlsColor('white'),
    border        : `1px solid ${dlsColor('caBlue')}`,
    display       : 'flex',
    flexDirection : 'row'
  }, {
    gap                : '4px',
    width              : 'auto !important',
    height             : '24px !important',
    display            : 'flex',
    padding            : '4px',
    boxShadow          : dlsShadow(3),
    marginLeft         : '20px',
    marginRight        : '0 !important',
    borderRadius       : '4px !important',
    backgroundColor    : dlsColor('paleBlue'),
    '& .MuiChip-label' : {
      color         : `${dlsColor('grey2')} !important`,
      fontSize      : '12px !important',
      fontFamily    : 'Inter !important',
      fontWeight    : 600,
      lineHeight    : '16px !important',
      letterSpacing : '0px !important'
    },
    '&&' : {
      '&:focus, &:hover' : {
        border     : 'transparent !important',
        background : `${dlsColor('paleBlue')} !important`
      }
    }
  }),
  chipLabel : {
    color : dlsColor('grey2')
  },
  chipDeleteIcon : toggleThemeStyle({
    margin : '0 !important',
    height : 16
  }, {
    width           : '16px',
    height          : '16px',
    margin          : '0 !important',
    borderRadius    : '4px',
    backgroundColor : '#1F90AE40'
  })
}));

const HIDDEN_QUERY = 'hidden-queries';

const normalizeQuery = (query: QueryChipData): QueryChipData => ({
  ...query,
  isExactQuery : isExactQuery(query)
});

/**
 * Defines a reusable Query Chips.
 *
 * ```typescript
 * import React from 'react';
 * import {QueryChips} from '@ion/features/src/search/';
 *
 * <QueryChips queries={chips} onRemove={onQueryRemove} />
 * ```
 */

export const QueryChips = ({
  queries,
  pageName,
  onRemove,
  onClickQueryChip,
  disabled = false,
  showCount = 2
}: QueryChipsProps) => {
  const classes = styles();
  const chipClasses = {
    root       : classes.chipRoot,
    label      : classes.chipLabel,
    deleteIcon : classes.chipDeleteIcon
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open                    = Boolean(anchorEl);

  const onIconClick = useCallback(function({currentTarget}) {
    setAnchorEl(currentTarget);
  }, []);

  const handleClose = useCallback(function() {
    setAnchorEl(null);
  }, []);

  const onChipRemove = useCallback(function(isPopOver = false) {
    return function(query: QueryChipData) {
      if (query.disableDelete) {
        return undefined;
      }
      return function() {
        onRemove?.(query.attribute)?.();
        if (isPopOver && queries.length <= 3) {
          handleClose();
        }
      };
    };
  }, [handleClose, onRemove, queries.length]);

  return (
    <>
      {!isEmpty(queries) && queries.slice(0, showCount)
        .map((query: QueryChipData, index: number) => {
          const normalizedQuery = normalizeQuery(query);
          return (
            <SimpleChip key={`${query.attribute}-${index}`}
              classes={chipClasses}
              query={normalizedQuery}
              pageName={pageName}
              radius={56}
              onRemove={onChipRemove(false)(query)}
              onClickChip={onClickQueryChip}
              disabled={disabled} />
          );
        })}
      {queries?.length > showCount && (
        <Button classes={{root: classes.iconButton}}
          onClick={onIconClick}
          size='small'
          startIcon={<Add />}>
          <span>{translate('common:modularsearch.more', {count: queries?.length - showCount})}</span>
        </Button>
      )}
      {open && queries.length > 2 && (<Menu id={HIDDEN_QUERY}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        anchorOrigin={{
          vertical   : 'top',
          horizontal : 'left'
        }}
        classes={{paper: classes.paper, list: classes.list}}
        onClose={handleClose}>
        {queries.slice(showCount, queries.length).map((query: QueryChipData, index: number) => {
          const normalizedQuery = normalizeQuery(query);
          return (
            <MenuItem key={`${query.attribute}-${3+index}`}
              classes={{root: classes.listItem}}>
              <SimpleChip classes={chipClasses}
                query={normalizedQuery}
                pageName={pageName}
                radius={56}
                onClickChip={onClickQueryChip}
                onRemove={onChipRemove(true)(query)}
                disabled={disabled} />
            </MenuItem>
          );
        })}
      </Menu>
      )}
    </>
  );
};
